import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { withTheme } from 'emotion-theming';
import find from 'lodash/find';
import get from 'lodash/get';

import LazyBackgroundImage from '../Common/LazyBackgroundImage';

import LandingPageThemes from '../../styling/themes/LandingPageThemes';
import { PadComponent } from '../../styling/layout-containers/LandingPageContainers';
import colors from '../../styling/colors';
import {
  MOBILE_END_QUERY,
  DESKTOP_START_QUERY,
  DESKTOP_BELOW_MAX_QUERY,
} from '../../styling/mixins/responsive';

import withTrackingPropsProvider from '../../hocs/withTrackingPropsProvider';

const SlatWrapper = styled.div`
  width: 100%;

  @media (${DESKTOP_START_QUERY}) and (${DESKTOP_BELOW_MAX_QUERY}) {
    ${({ theme, isIndented }) =>
      isIndented ? PadComponent(theme) : undefined};
  }
  ${({ theme }) => get(theme, 'Slat.marginBottomCss', ``)};
`;

const ColorWrapper = styled.div`
  width: 100%;
  color: ${({ color }) => colors(color)};
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const ContentContainer = styled(LazyBackgroundImage)`
  background-position: center center;
  background-size: cover;
  display: flex;
  margin: auto;
  max-width: 90rem;
  flex-wrap: wrap;
  flex-direction: ${({ mobileLayout }) =>
    mobileLayout === 'columnReverse' ? 'column-reverse' : 'row'};
  justify-content: ${({ header }) => (header ? 'center' : 'flex-around')};
  ${({ header }) => (header ? 'align-items: flex-start;' : null)};

  ${({ mobileHeight }) => {
    return `
      @media (${MOBILE_END_QUERY}) {
        min-height: ${mobileHeight};
        max-height: ${mobileHeight};
      }
    `;
  }};

  @media (${MOBILE_END_QUERY}) {
    background-image: none;
  }

  @media (${DESKTOP_START_QUERY}) {
    flex-direction: ${({ layout }) =>
      layout === 'left' ? 'row-reverse' : 'row'};
    min-height: ${({ height }) => height};
    max-height: ${({ height }) => height};
  }

  /* IE11 hack, fixes issue with min-height & height interaction with flexbox */
  @media all and (${DESKTOP_START_QUERY}) and (-ms-high-contrast: active),
    all and (${DESKTOP_START_QUERY}) and (-ms-high-contrast: none) {
    height: ${({ height }) => height};
  }
`;

/**
 * BaseSlat
 * @description
 * This component is wrapper for most banner like content on landing pages. It will render its
 * children with the given layout.
 *
 * @property {string} backgroundColor
 * @property {string} backgroundImage
 * @property {string} className
 * @property {string} color - Color of text within the component
 * @property {string} media - object contains urls for either video or image to put in slat
 * @property {string} id - html id used for linking to component on the page
 * @property {string} type - id of the theme that we will use for this slat
 * @property {string} height - Height of slat, (auto, 240, 320, 480, 520, 560, 640)
 * @property {string} layout - Positioning of the text content in the slat (left, right, center)
 * @property {func or node} content - Content that is intended to appear in the "content" side of the slat
 * @property {func or node} media - Content that is intended to appear in the "content" side of the slat
 * @property {object} tracking - Key-value pairs that all child trackable objects will report
 */

export class BaseSlat extends Component {
  static propTypes = {
    backgroundColor: PropTypes.string,
    backgroundImage: PropTypes.string,
    className: PropTypes.string,
    color: PropTypes.string,
    id: PropTypes.string,
    type: PropTypes.string,
    height: PropTypes.oneOf([
      'auto',
      '240px',
      '300px',
      '320px',
      '400px',
      '410px',
      '420px',
      '480px',
      '520px',
      '560px',
      '600px',
      '640px',
    ]),
    layout: PropTypes.oneOf(['left', 'right', 'center', 'custom']),
    mobileLayout: PropTypes.oneOf(['row', 'columnReverse']),
    content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    media: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    tracking: PropTypes.object.isRequired,
  };

  static defaultProps = {
    color: 'lapel',
    height: 'auto',
    layout: 'center',
    mobileLayout: 'row',
    theme: LandingPageThemes.default,
  };

  render() {
    const {
      backgroundColor,
      backgroundImage,
      className,
      color,
      height,
      mobileHeight,
      id,
      layout,
      mobileLayout,
      content,
      media,
      header,
      theme,
    } = this.props;

    // Get the first isIndented value that is not undefined, true is base case
    let potentialIndentValues = [
      get(this, 'props.isIndented'),
      get(theme, 'Slat.isIndented'),
      true,
    ];
    let isIndented = find(
      potentialIndentValues,
      value => typeof value === 'boolean',
    );

    return (
      <SlatWrapper
        id={id}
        className={className}
        theme={theme}
        isIndented={isIndented}
      >
        <ColorWrapper color={color} backgroundColor={backgroundColor}>
          <ContentContainer
            className="content-container"
            header={header}
            backgroundImage={backgroundImage}
            height={height}
            mobileHeight={mobileHeight}
            layout={layout}
            mobileLayout={mobileLayout}
          >
            {typeof header === 'function' ? header() : header}
            {typeof media === 'function' ? media() : media}
            {typeof content === 'function' ? content() : content}
          </ContentContainer>
        </ColorWrapper>
      </SlatWrapper>
    );
  }
}

export default withTrackingPropsProvider(withTheme(BaseSlat));
